/* tu-reviews.js -- the product-page reviews section (sections/tu-product.liquid,
 * <section id="reviews" class="tu-rv">), 2026-09-14. Spec:
 * Docs/decisions/reviews-section.md.
 *
 * Liquid paints the whole section: summary, histogram, the first six cards,
 * the empty block and the contact form. This file reads the reviews from the
 * JSON in [data-tu-reviews] and adds what needs a script:
 *   - histogram rows filter to one star rating (press again to clear), with a
 *     chip in the controls row; "Verified only" chip if Liquid rendered one
 *   - the Sort dropdown (tu-sort markup, local behaviour: nothing navigates)
 *   - Load more, six at a time; Read more on cards whose body is clamped
 *   - the write-review form: collapsed until either "Write a review" button,
 *     star picker (radio group), live counter, inline validation
 *   - hides our chrome when Ryviu fills [data-tu-revs-widget] (spec D)
 * Self-inits on DOMContentLoaded and shopify:section:load with an el.tuBound
 * guard, like tu-scroller.js. No libraries. */
(() => {
  'use strict';

  const PAGE = 6;
  const MIN_BODY = 40;
  const MAX_BODY = 1000;
  const PATH = 'M10 1.6l2.6 5.4 5.9.7-4.3 4.1 1 5.9L10 14.9l-5.2 2.7 1-5.9-4.3-4.1 5.9-.7z';
  const STAR_ON = '<svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true"><path d="' + PATH + '" fill="currentColor"/></svg>';
  const STAR_OFF = '<svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true"><path d="' + PATH + '" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>';

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function bind(root) {
    if (root.tuBound) return;
    root.tuBound = true;
    root.dataset.tuRvBound = 'true';

    const q = (s) => root.querySelector(s);
    const qa = (s) => [...root.querySelectorAll(s)];

    /* ---------------- data ---------------- */
    let reviews = [];
    const dataEl = q('[data-tu-reviews]');
    if (dataEl) {
      try { reviews = JSON.parse(dataEl.textContent); } catch (e) { reviews = []; }
    }
    reviews = (Array.isArray(reviews) ? reviews : []).map((r, i) => {
      let ts = Date.parse(r.iso || r.date || '');
      if (Number.isNaN(ts)) ts = -i; // unparseable: keep source order, treat as oldest
      return { ...r, stars: Number(r.stars) || 0, verified: r.verified !== false, ts };
    });

    const state = { star: 0, verified: false, sort: 'recent', shown: PAGE };

    /* ---------------- elements ---------------- */
    const list = q('[data-tu-rv-list]');
    const controls = q('[data-tu-rv-controls]');
    const chips = q('[data-tu-rv-chips]');
    const result = q('[data-tu-rv-result]');
    const more = q('[data-tu-rv-more]');
    const moreN = q('[data-tu-rv-more-n]');
    const hist = q('[data-tu-rv-hist]');
    const rows = qa('[data-tu-rv-star]');
    const verifiedChip = q('[data-tu-rv-verified]');
    const sortRoot = q('[data-tu-rv-sort]');
    const formwrap = q('[data-tu-rv-formwrap]');
    const form = q('#tu-review-form');
    const openBtns = qa('[data-tu-rv-open]');

    /* ---------------- filter + sort ---------------- */
    const visible = () => {
      const a = reviews.filter((r) => (!state.star || r.stars === state.star) && (!state.verified || r.verified));
      if (state.sort === 'high') a.sort((x, y) => y.stars - x.stars || y.ts - x.ts);
      else if (state.sort === 'low') a.sort((x, y) => x.stars - y.stars || y.ts - x.ts);
      else a.sort((x, y) => y.ts - x.ts);
      return a;
    };

    const card = (r, i) => (
      '<article class="tu-rv__card tu-rv__card--anim" style="--i:' + Math.min(i, 8) + '" aria-label="' + esc(r.title) + '">' +
        '<div class="tu-rv__card-top">' +
          '<span class="tu-pdp__stars tu-pdp__stars--fill" style="--tu-fill:' + (r.stars * 20) + '%" role="img" aria-label="' + r.stars + ' out of 5 stars">' +
            '<span class="tu-pdp__stars-on">' + STAR_ON.repeat(5) + '</span>' +
            '<span class="tu-pdp__stars-off" aria-hidden="true">' + STAR_OFF.repeat(5) + '</span>' +
          '</span>' +
          (r.verified ? '<span class="tu-rv__card-v">Verified buyer</span>' : '') +
        '</div>' +
        '<h3 class="tu-rv__card-t">' + esc(r.title) + '</h3>' +
        '<p class="tu-rv__card-b">' + esc(r.body) + '</p>' +
        '<button type="button" class="tu-rv__more-link tu-pdp__link" aria-expanded="false" hidden>Read more &rsaquo;</button>' +
        '<p class="tu-rv__card-by">' + esc(r.name) + ' &middot; ' + esc(r.date) + '</p>' +
      '</article>'
    );

    // Read more: shown only on a card whose body actually overflows its clamp.
    const measureClamps = () => {
      qa('.tu-rv__card').forEach((c) => {
        const body = c.querySelector('.tu-rv__card-b');
        const btn = c.querySelector('.tu-rv__more-link');
        if (!body || !btn) return;
        if (body.classList.contains('is-open')) return;
        btn.hidden = !(body.scrollHeight > body.clientHeight + 1);
      });
    };

    const render = (animate) => {
      if (!list) return;
      const all = visible();
      const shown = all.slice(0, state.shown);
      list.innerHTML = shown.map(card).join('');
      if (animate && !reduced()) {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          qa('.tu-rv__card--anim').forEach((c) => c.classList.add('is-in'));
        }));
      } else {
        qa('.tu-rv__card--anim').forEach((c) => c.classList.add('is-in'));
      }
      measureClamps();

      if (more) {
        const left = all.length - shown.length;
        more.hidden = left <= 0;
        if (moreN) moreN.textContent = shown.length + ' of ' + all.length;
      }
      if (result) {
        let t = shown.length + ' of ' + all.length + ' ' + (all.length === 1 ? 'review' : 'reviews');
        if (state.star) t += ' · ' + state.star + (state.star === 1 ? ' star' : ' stars');
        if (state.verified) t += ' · verified';
        result.textContent = t;
      }
      if (chips) {
        chips.innerHTML = state.star
          ? '<button type="button" class="tu-rv__chip" data-tu-rv-clear aria-label="Clear the ' + state.star + '-star filter">' + state.star + (state.star === 1 ? ' star' : ' stars') + ' <span aria-hidden="true">&times;</span></button>'
          : '';
      }
      rows.forEach((b) => b.setAttribute('aria-pressed', Number(b.dataset.tuRvStar) === state.star ? 'true' : 'false'));
      root.dataset.tuRvFiltered = state.star ? String(state.star) : '';
    };

    const setStar = (n) => {
      state.star = state.star === n ? 0 : n;
      state.shown = PAGE;
      render(true);
    };

    rows.forEach((b) => b.addEventListener('click', () => setStar(Number(b.dataset.tuRvStar))));

    root.addEventListener('click', (e) => {
      const clear = e.target.closest('[data-tu-rv-clear]');
      if (clear) { state.star = 0; state.shown = PAGE; render(true); return; }
      const rm = e.target.closest('.tu-rv__more-link');
      if (rm) {
        const body = rm.parentElement.querySelector('.tu-rv__card-b');
        const open = !body.classList.contains('is-open');
        body.classList.toggle('is-open', open);
        rm.setAttribute('aria-expanded', open ? 'true' : 'false');
        rm.innerHTML = open ? 'Show less &rsaquo;' : 'Read more &rsaquo;';
      }
    });

    if (verifiedChip) {
      verifiedChip.addEventListener('click', () => {
        state.verified = !state.verified;
        verifiedChip.setAttribute('aria-pressed', state.verified ? 'true' : 'false');
        state.shown = PAGE;
        render(true);
      });
    }

    if (more) {
      more.addEventListener('click', () => {
        state.shown += PAGE;
        render(true);
      });
    }

    /* ---------------- sort dropdown (tu-sort markup, local behaviour) ----------------
       assets/tu-sort.js listens at the document for any .tu-sort__btn click and
       for [data-tu-sort-option]; this root carries neither data attribute, and
       propagation stops here so tu-sort.js never sees the click (it would throw
       on a root without [data-tu-sort]). */
    if (sortRoot) {
      const OPEN = 'is-open';
      const btn = sortRoot.querySelector('.tu-sort__btn');
      const val = sortRoot.querySelector('[data-tu-rv-sort-value]');
      const items = () => [...sortRoot.querySelectorAll('[data-tu-rv-sort-option]')];
      const open = () => {
        if (sortRoot.classList.contains(OPEN)) return;
        sortRoot.classList.add(OPEN);
        btn.setAttribute('aria-expanded', 'true');
        const sel = sortRoot.querySelector('[aria-selected="true"]') || items()[0];
        if (sel) sel.focus();
      };
      const close = (refocus) => {
        if (!sortRoot.classList.contains(OPEN)) return;
        sortRoot.classList.remove(OPEN);
        btn.setAttribute('aria-expanded', 'false');
        if (refocus) btn.focus();
      };
      const pick = (li) => {
        state.sort = li.getAttribute('data-tu-rv-sort-option');
        items().forEach((x) => x.setAttribute('aria-selected', x === li ? 'true' : 'false'));
        if (val) val.textContent = li.textContent.trim();
        close(true);
        state.shown = PAGE;
        render(true);
      };

      sortRoot.addEventListener('click', (e) => {
        e.stopPropagation();
        if (e.target.closest('.tu-sort__btn')) { sortRoot.classList.contains(OPEN) ? close(false) : open(); return; }
        const li = e.target.closest('[data-tu-rv-sort-option]');
        if (li) { e.preventDefault(); pick(li); }
      });
      sortRoot.addEventListener('keydown', (e) => {
        e.stopPropagation();
        const list = items();
        const i = list.indexOf(document.activeElement);
        switch (e.key) {
          case 'Escape': close(true); e.preventDefault(); break;
          case 'ArrowDown':
            e.preventDefault();
            if (!sortRoot.classList.contains(OPEN)) { open(); break; }
            (list[Math.min(i + 1, list.length - 1)] || list[0]).focus(); break;
          case 'ArrowUp': e.preventDefault(); (list[Math.max(i - 1, 0)] || list[0]).focus(); break;
          case 'Home': e.preventDefault(); if (list[0]) list[0].focus(); break;
          case 'End': e.preventDefault(); if (list.length) list[list.length - 1].focus(); break;
          case 'Enter': case ' ': if (i >= 0) { e.preventDefault(); pick(list[i]); } break;
          case 'Tab': close(false); break;
          default: break;
        }
      });
      document.addEventListener('click', (e) => { if (!sortRoot.contains(e.target)) close(false); });
      document.addEventListener('focusin', (e) => { if (!sortRoot.contains(e.target)) close(false); });
    }

    /* ---------------- form: open / close ---------------- */
    const setOpen = (open, focusFirst) => {
      if (!formwrap) return;
      formwrap.classList.toggle('is-open', open);
      openBtns.forEach((b) => b.setAttribute('aria-expanded', open ? 'true' : 'false'));
      if (open) {
        formwrap.scrollIntoView({ block: 'nearest', behavior: reduced() ? 'auto' : 'smooth' });
        if (focusFirst) {
          const first = formwrap.querySelector('[data-tu-rv-rate]') || formwrap.querySelector('[data-tu-rv-ok]');
          if (first) first.focus({ preventScroll: true });
        }
      }
    };
    openBtns.forEach((b) => b.addEventListener('click', () => setOpen(!formwrap.classList.contains('is-open'), true)));

    // Landing after a post (?contact_posted=true#tu-review-form) or with
    // Shopify's own errors: the block must be visible, so open on bind.
    const posted = !!q('[data-tu-rv-ok]') || !!q('[data-tu-rv-formerr]');

    /* ---------------- form: star picker + validation ---------------- */
    if (form && !posted) {
      form.noValidate = true;
      const starBtns = qa('[data-tu-rv-rate]');
      const starsInput = q('[data-tu-rv-stars]');
      const body = q('[data-tu-rv-body]');
      const counter = q('[data-tu-rv-counter]');
      const name = q('[data-tu-rv-name]');
      const email = q('[data-tu-rv-email]');
      const err = (key) => q('[data-tu-rv-err="' + key + '"]');

      const setErr = (key, msg) => {
        const el = err(key);
        if (!el) return;
        el.textContent = msg || '';
        el.hidden = !msg;
        const field = el.closest('.tu-field');
        if (field) field.classList.toggle('tu-field--err', !!msg);
      };

      const rate = (n, focus) => {
        if (starsInput) starsInput.value = n ? String(n) : '';
        starBtns.forEach((b) => {
          const v = Number(b.dataset.tuRvRate);
          b.setAttribute('aria-checked', v === n ? 'true' : 'false');
          b.classList.toggle('is-on', v <= n);
          b.tabIndex = (n ? v === n : v === 1) ? 0 : -1;
          if (focus && v === n) b.focus();
        });
        if (n) setErr('stars', '');
      };
      starBtns.forEach((b) => {
        b.addEventListener('click', () => rate(Number(b.dataset.tuRvRate), false));
        b.addEventListener('keydown', (e) => {
          const cur = Number(starsInput && starsInput.value) || Number(b.dataset.tuRvRate);
          if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { e.preventDefault(); rate(Math.min(5, cur + 1), true); }
          if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { e.preventDefault(); rate(Math.max(1, cur - 1), true); }
        });
      });
      // hover preview on a pointer device
      starBtns.forEach((b) => {
        b.addEventListener('mouseenter', () => { const v = Number(b.dataset.tuRvRate); starBtns.forEach((x) => x.classList.toggle('is-hover', Number(x.dataset.tuRvRate) <= v)); });
        b.addEventListener('mouseleave', () => starBtns.forEach((x) => x.classList.remove('is-hover')));
      });

      const count = () => {
        if (!body || !counter) return;
        const n = body.value.length;
        const need = Math.max(0, MIN_BODY - n);
        counter.textContent = n.toLocaleString('en-US') + ' / ' + MAX_BODY.toLocaleString('en-US') + (need ? ' · ' + need + ' more to go' : '');
        if (n >= MIN_BODY) setErr('body', '');
      };
      if (body) { body.addEventListener('input', count); count(); }
      if (name) name.addEventListener('input', () => { if (name.value.trim()) setErr('name', ''); });
      if (email) email.addEventListener('input', () => { if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())) setErr('email', ''); });

      // Validate on the BUTTON, then requestSubmit(). Shopify's spam protection
      // (storefront-forms-hcaptcha) hooks the form's submit event and opens the
      // hCaptcha challenge before any bubble-phase listener runs, so validating
      // in 'submit' let an empty form reach the captcha (seen on the phone
      // 2026-09-14). With the button demoted to type=button the submit event
      // only fires after the fields pass; without the script it stays a real
      // submit button and Shopify's own checks apply.
      const validate = () => {
        let first = null;
        const fail = (key, msg, el) => { setErr(key, msg); if (!first) first = el; };
        if (!starsInput || !starsInput.value) fail('stars', 'Pick a star rating.', starBtns[0]);
        const len = body ? body.value.trim().length : 0;
        if (len < MIN_BODY) fail('body', 'Tell the next shooter a little more — at least ' + MIN_BODY + ' characters.', body);
        else if (len > MAX_BODY) fail('body', 'Keep it under ' + MAX_BODY.toLocaleString('en-US') + ' characters.', body);
        if (!name || !name.value.trim()) fail('name', 'Add your name, or just a first name and initial.', name);
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())) fail('email', 'We need an email to check the review. It is never shown.', email);
        if (first) { first.focus(); return false; }
        return true;
      };
      const submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn) {
        submitBtn.type = 'button';
        submitBtn.addEventListener('click', () => {
          if (!validate()) return;
          submitBtn.setAttribute('aria-disabled', 'true');
          submitBtn.textContent = 'Sending…';
          if (typeof form.requestSubmit === 'function') form.requestSubmit(); else form.submit();
        });
      }
      // safety net for any other path into submit (Enter in a field, other scripts)
      form.addEventListener('submit', (e) => {
        if (!validate()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          if (submitBtn) { submitBtn.removeAttribute('aria-disabled'); submitBtn.textContent = 'Send review'; }
        }
      }, true);
    }

    /* ---------------- Ryviu takes over (spec D) ----------------
       tu-pdp.js already hides its own fallback on the same signal; this
       observer hides our summary, controls, list, foot, empty block and form
       the moment the app fills its div. Never on a timer. */
    const widget = q('[data-tu-revs-widget]');
    if (widget) {
      const takeOver = () => {
        if (!widget.children.length) return;
        root.dataset.tuRvApp = 'true';
        ['[data-tu-rv-summary]', '[data-tu-rv-controls]', '[data-tu-rv-list]', '[data-tu-rv-foot]', '[data-tu-rv-empty]', '[data-tu-rv-formwrap]']
          .forEach((s) => { const el = q(s); if (el) el.hidden = true; });
      };
      new MutationObserver(takeOver).observe(widget, { childList: true });
      takeOver();
    }

    /* ---------------- first paint ---------------- */
    if (controls) controls.hidden = false;
    if (hist) requestAnimationFrame(() => hist.classList.add('is-in'));
    render(false);
    setOpen(posted, false);
    if (posted) {
      const ok = q('[data-tu-rv-ok]');
      if (ok) requestAnimationFrame(() => ok.scrollIntoView({ block: 'center', behavior: 'auto' }));
    }

    let raf = 0;
    window.addEventListener('resize', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(measureClamps); }, { passive: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measureClamps);
  }

  function init() { document.querySelectorAll('[data-tu-rv]').forEach(bind); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
